// Demo data for tab 3 (LH-F2, rewritten in LH-F5): the lenders' API records, their API batches as real Node projects
// (one REST endpoint per file under api/, written against @firstfund/lender-sdk), the captured lender codes, the
// standard test applicant. Every figure is made up; the code is short but real: the sandbox runs it. No secret
// anywhere: the lender's variables come from the Env variables tab as ctx.env.NAME (placeholders in the demo data).
import { appTemplate, packageJsonTemplate, serverTemplate } from '@/lib/endpoints'
import { staticDiscover } from '@/lib/manifest'
import type { Batch, CapturedCode, CodeFile, LenderApi, TestEvent } from '@/types'

/** The standard input (G10) as the playground's test applicant. Demo values, not a person. */
export const TEST_APPLICANT: Record<string, unknown> = {
  ref: 'rec_demo_1',
  mobile: '9800000010',
  pan: 'ABCDE1234F',
  name: 'Test Applicant',
  dob: '1990-01-01',
  email: 'test.applicant@example.com',
  incomeMonth: 25000,
  employment: 'salaried',
  pincode: '560001',
  city: 'Bengaluru',
  loanAmount: 200000,
  consentId: 'cmp_demo_consent_1',
  consentAt: '2026-10-30T10:00:00+05:30',
  ip: '203.0.113.10',
  userAgent: 'Mozilla/5.0 (Linux; Android 14) Demo',
}

const SDK = "const { defineApi } = require('@firstfund/lender-sdk')"

// ---- MoneyView: src/api/v2/*.js, src/services/moneyview.js ------------------------------------------------------------------------

const MV_LIB = `/** src/services/moneyview.js: what every MoneyView v2 call shares. LENDER_TOKEN is the bearer the Gateway keeps fresh from /v2/token. */
function bearer(ctx) {
  return { Authorization: 'Bearer ' + ctx.env.LENDER_TOKEN, 'Content-Type': 'application/json' }
}
function url(ctx, path) {
  return ctx.env.LENDER_BASE_URL + path
}
module.exports = { bearer, url }
`

const MV_HEALTH = `${SDK}

/** Health check: GET /health at the lender. Called by the monitor every 60 s, never by a workflow. */
module.exports = defineApi({
  title: 'Health check',
  method: 'GET',
  outcomes: ['ok', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({ method: 'GET', url: ctx.env.LENDER_BASE_URL + '/health', headers: {}, timeoutMs: 3000 })
    if (reply.status === 200 && reply.body && reply.body.status === 'UP') return { outcome: 'ok', reply_code: 'UP' }
    return { outcome: 'error', reply_code: 'HTTP_' + reply.status }
  },
})
`

const MV_TOKEN = `${SDK}

/** Auth token (token source): POST /oauth/token. The Gateway calls this itself and keeps LENDER_TOKEN fresh (55 min);
 *  a workflow never calls it. PARTNER_ID and CLIENT_SECRET come from the Env variables tab. */
module.exports = defineApi({
  title: 'Auth token',
  tokenSource: true,
  outcomes: ['ok', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({
      method: 'POST',
      url: ctx.env.LENDER_BASE_URL + '/oauth/token',
      headers: { 'Content-Type': 'application/json' },
      body: { grant_type: 'client_credentials', partner_id: ctx.secret('PARTNER_ID'), client_secret: ctx.secret('CLIENT_SECRET') },
      timeoutMs: 5000,
    })
    const b = reply.body || {}
    if (reply.status === 200 && b.access_token) return { outcome: 'ok', reply_code: 'TOKEN', token: b.access_token, expiresInS: b.expires_in || 3600 }
    if (reply.status === 401) return { outcome: 'error', reply_code: 'BAD_CREDENTIALS', message: 'The lender refused our keys.' }
    return { outcome: 'error', reply_code: 'HTTP_' + reply.status }
  },
})
`

const MV_DEDUPE = `${SDK}
const { bearer, url } = require('../../services/moneyview')

/** Dedupe check: POST /lead/dedupe. Does the lender already know this person? */
module.exports = defineApi({
  title: 'Dedupe check',
  outcomes: ['ok', 'duplicate', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({ method: 'POST', url: url(ctx, '/lead/dedupe'), headers: bearer(ctx), body: { mobile: applicant.mobile, pan: applicant.pan }, timeoutMs: 5000 })
    const b = reply.body || {}
    if (reply.status === 200 && b.exists === false) return { outcome: 'ok', reply_code: 'NEW_CUSTOMER' }
    if (reply.status === 200 && b.exists === true) return { outcome: 'duplicate', reply_code: 'DUP_CUSTOMER', message: 'You already have an application with this lender.' }
    return { outcome: 'error', reply_code: 'HTTP_' + reply.status }
  },
})
`

const MV_CREATE_LEAD_120 = `${SDK}
const { bearer, url } = require('../../services/moneyview')

/** Create lead (pre-fill): POST /lead/create. */
module.exports = defineApi({
  title: 'Create lead (pre-fill)',
  outcomes: ['ok', 'duplicate', 'rejected', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({
      method: 'POST',
      url: url(ctx, '/lead/create'),
      headers: bearer(ctx),
      body: {
        partner_ref: ctx.ref,
        mobile: applicant.mobile,
        pan: applicant.pan,
        name: applicant.name,
        dob: applicant.dob,
        monthly_income: applicant.incomeMonth,
        employment: applicant.employment,
        pincode: applicant.pincode,
        consent_id: applicant.consentId,
      },
      timeoutMs: 8000,
    })
    return parse(reply)
  },
})

function parse(reply) {
  const b = reply.body || {}
  if (reply.status === 0) return { outcome: 'error', reply_code: 'TIMEOUT', message: 'The lender did not answer.' }
  if (reply.status === 201 && b.status === 'LEAD_CREATED') return { outcome: 'ok', reply_code: 'LEAD_CREATED', lender_ref: b.lead_id }
  if (reply.status === 409 || b.code === 'DUP_CUSTOMER') return { outcome: 'duplicate', reply_code: 'DUP_CUSTOMER', message: 'Already applied with this lender.' }
  if (reply.status === 422 && b.code === 'LOW_INCOME') return { outcome: 'rejected', reply_code: 'LOW_INCOME', message: 'Income below the minimum.' }
  return { outcome: 'error', reply_code: 'HTTP_' + reply.status + (b.code ? '_' + b.code : ''), message: 'Unexpected reply.' }
}
`

// 1.3.0 draft: better messages (commit c41e0a7). The INVALID_PAN branch is still missing: the "Wrong PAN" test is red
// until someone adds `if (reply.status === 422 && b.code === 'INVALID_PAN') return { outcome: 'rejected', ... }`.
const MV_CREATE_LEAD_130 = MV_CREATE_LEAD_120
  .replace("message: 'Already applied with this lender.'", "message: 'You already have an application with MoneyView. Continue it in their app.'")
  .replace("message: 'Income below the minimum.'", "message: 'MoneyView needs a monthly income of ₹13,500 or more.'")
  .replace("message: 'The lender did not answer.'", "message: 'MoneyView did not answer. Try again in a minute.'")

const MV_OFFERS = `${SDK}
const { bearer, url } = require('../../services/moneyview')

/** Check offers: POST /lead/{id}/offer. Needs the lead id from create-lead (ctx.prev.lender_ref). */
module.exports = defineApi({
  title: 'Check offers',
  outcomes: ['offer', 'approved', 'pending', 'rejected', 'error'],
  async handler(applicant, ctx) {
    const leadId = ctx.prev && ctx.prev.lender_ref
    const reply = await ctx.lender({ method: 'POST', url: url(ctx, '/lead/' + leadId + '/offer'), headers: bearer(ctx), body: { requested_amount: applicant.loanAmount }, timeoutMs: 12000 })
    const b = reply.body || {}
    if (reply.status === 200 && Array.isArray(b.offers) && b.offers.length) {
      const offers = b.offers.map(function (o) { return { amount: o.amount, tenureMonths: o.tenure, ratePa: o.roi, emi: o.emi } })
      return { outcome: 'offer', reply_code: 'OFFERS', offers: offers, link: b.redirect_url }
    }
    if (reply.status === 200 && b.status === 'PRE_APPROVED') return { outcome: 'approved', reply_code: 'PRE_APPROVED', link: b.redirect_url }
    if (reply.status === 202 || b.status === 'PENDING') return { outcome: 'pending', reply_code: 'PENDING', retryAfterMs: 8000 }
    if (reply.status === 200 && b.status === 'REJECTED') return { outcome: 'rejected', reply_code: b.reason || 'REJECTED', message: 'No offer from this lender today.' }
    return { outcome: 'error', reply_code: 'HTTP_' + reply.status + (b.code ? '_' + b.code : '') }
  },
})
`

// 1.1.0 had no "pending" on offers yet (commit 3f0ab77 in 1.2.0 added it): fewer ports, which C1 notices
const MV_OFFERS_110 = MV_OFFERS.replace("outcomes: ['offer', 'approved', 'pending', 'rejected', 'error'],", "outcomes: ['offer', 'approved', 'rejected', 'error'],").replace("    if (reply.status === 202 || b.status === 'PENDING') return { outcome: 'pending', reply_code: 'PENDING', retryAfterMs: 8000 }\n", '')

const MV_STATUS = `${SDK}
const { bearer, url } = require('../../services/moneyview')

/** Status: GET /lead/{id}/status. Polled while the lender decides. */
module.exports = defineApi({
  title: 'Status',
  outcomes: ['approved', 'rejected', 'pending', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({ method: 'GET', url: url(ctx, '/lead/' + (ctx.prev && ctx.prev.lender_ref) + '/status'), headers: bearer(ctx), timeoutMs: 5000 })
    const b = reply.body || {}
    if (reply.status === 200 && b.status === 'APPROVED') return { outcome: 'approved', reply_code: 'APPROVED', link: b.redirect_url }
    if (reply.status === 200 && b.status === 'REJECTED') return { outcome: 'rejected', reply_code: b.reason || 'REJECTED' }
    if (reply.status === 200 && b.status === 'IN_PROGRESS') return { outcome: 'pending', reply_code: 'IN_PROGRESS', retryAfterMs: 10000 }
    return { outcome: 'error', reply_code: 'HTTP_' + reply.status }
  },
})
`

const MV_PRODUCTS = `${SDK}
const { bearer, url } = require('../../services/moneyview')

/** Fetch products: GET /products. Which products the lender offers today (salary advance on / off). */
module.exports = defineApi({
  title: 'Fetch products',
  method: 'GET',
  outcomes: ['ok', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({ method: 'GET', url: url(ctx, '/products'), headers: bearer(ctx), timeoutMs: 5000 })
    const b = reply.body || {}
    if (reply.status === 200 && Array.isArray(b.products)) return { outcome: 'ok', reply_code: 'PRODUCTS', products: b.products.map(function (p) { return p.code }) }
    return { outcome: 'error', reply_code: 'HTTP_' + reply.status }
  },
})
`

const MV_OLD_LEAD = `${SDK}

/** Lead create (old v1 API, api-key header). Retired with pl.flow 1.1.0 on 2026-09-30; kept for the record. */
module.exports = defineApi({
  title: 'Lead create (old)',
  outcomes: ['ok', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({ method: 'POST', url: ctx.env.LENDER_BASE_URL + '/v1/lead', headers: { 'x-api-key': ctx.secret('LENDER_API_KEY') }, body: { mobile: applicant.mobile, pan: applicant.pan }, timeoutMs: 8000 })
    if (reply.status === 200) return { outcome: 'ok', reply_code: 'OK', lender_ref: reply.body && reply.body.id }
    return { outcome: 'error', reply_code: 'HTTP_' + reply.status }
  },
})
`

const MV_OLD_ELIGIBILITY = `${SDK}

/** Eligibility (old v1 API). The lender removed it on 2026-08-12 (HTTP 410). Unused. */
module.exports = defineApi({
  title: 'Eligibility (old)',
  outcomes: ['ok', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({ method: 'POST', url: ctx.env.LENDER_BASE_URL + '/v1/eligibility', headers: { 'x-api-key': ctx.secret('LENDER_API_KEY') }, body: { pan: applicant.pan }, timeoutMs: 5000 })
    return { outcome: reply.status === 200 ? 'ok' : 'error', reply_code: 'HTTP_' + reply.status }
  },
})
`

const MV_README = `# moneyview.batchApi

A Node service: \`src/server.js\` starts it (\`npm start\`), \`src/app.js\` mounts every endpoint, one REST endpoint per file under \`src/api/\` (folder = route), the lender client in \`src/services/\`; written with \`@firstfund/lender-sdk\`.
\`npm start\` runs it on :8080 in the sandbox; Build makes the container image the Gateway runs.

| Endpoint | File | Calls at the lender | Outcomes |
|---|---|---|---|
| POST /v2/token | src/api/v2/token.js | POST /oauth/token (token source, the Gateway calls it) | ok · error |
| GET /health | src/api/health.js | GET /health (monitor) | ok · error |
| POST /v2/dedupe | src/api/v2/dedupe.js | POST /lead/dedupe | ok · duplicate · error |
| POST /v2/create-lead | src/api/v2/create-lead.js | POST /lead/create | ok · duplicate · rejected · error |
| POST /v2/offers | src/api/v2/offers.js | POST /lead/{id}/offer | offer · approved · pending · rejected · error |
| POST /v2/status | src/api/v2/status.js | GET /lead/{id}/status | approved · rejected · pending · error |
| GET /v2/products | src/api/v2/products.js | GET /products | ok · error |
| /v1/* | src/api/v1/ | the old API (kept) | — |

Rules: call the lender only through \`ctx.lender(call)\` (that is what makes test events, capture and the system check
work); no key in the code: \`ctx.env.NAME\` / \`ctx.secret('NAME')\` come from the Env variables tab; an earlier step's
outcome arrives as \`ctx.prev\`.
`

const mvFiles = (createLead: string, withProducts: boolean, version: string, offers = MV_OFFERS): CodeFile[] => [
  { path: 'package.json', content: packageJsonTemplate('moneyview', version) }, { path: 'src/server.js', content: serverTemplate() }, { path: 'src/app.js', content: appTemplate() },
  { path: 'README.md', content: MV_README },
  { path: 'src/services/moneyview.js', content: MV_LIB },
  { path: 'src/api/health.js', content: MV_HEALTH },
  { path: 'src/api/v2/token.js', content: MV_TOKEN },
  { path: 'src/api/v2/dedupe.js', content: MV_DEDUPE },
  { path: 'src/api/v2/create-lead.js', content: createLead },
  { path: 'src/api/v2/offers.js', content: offers },
  { path: 'src/api/v2/status.js', content: MV_STATUS },
  ...(withProducts ? [{ path: 'src/api/v2/products.js', content: MV_PRODUCTS }] : []),
  { path: 'src/api/v1/lead.js', content: MV_OLD_LEAD },
  { path: 'src/api/v1/eligibility.js', content: MV_OLD_ELIGIBILITY },
]

const t = (id: string, endpoint: string, name: string, status: number, body: unknown, expectedOutcome: string, source: TestEvent['source'] = 'sample'): TestEvent => ({ id, endpoint, name, reply: { status, body }, expectedOutcome, source })

const MV_TESTS_120: TestEvent[] = [
  t('mv_t_token_ok', '/v2/token', 'Token issued', 200, { access_token: 'demo-token', expires_in: 3300 }, 'ok'),
  t('mv_t_health_ok', '/health', 'Lender up', 200, { status: 'UP' }, 'ok'),
  t('mv_t_health_down', '/health', 'Lender down', 503, { status: 'DOWN' }, 'error'),
  t('mv_t_dedupe_new', '/v2/dedupe', 'New customer', 200, { exists: false }, 'ok'),
  t('mv_t_dedupe_dup', '/v2/dedupe', 'Duplicate customer', 200, { exists: true, code: 'DUP_CUSTOMER' }, 'duplicate'),
  t('mv_t_create_ok', '/v2/create-lead', 'Lead created', 201, { status: 'LEAD_CREATED', lead_id: 'MV-9001' }, 'ok'),
  t('mv_t_create_dup', '/v2/create-lead', 'Duplicate customer', 409, { code: 'DUP_CUSTOMER' }, 'duplicate'),
  t('mv_t_create_low', '/v2/create-lead', 'Low income', 422, { code: 'LOW_INCOME' }, 'rejected'),
  t('mv_t_create_timeout', '/v2/create-lead', 'No reply (timeout)', 0, null, 'error'),
  t('mv_t_offers_list', '/v2/offers', 'Two offers', 200, { offers: [{ amount: 200000, tenure: 24, roi: 18, emi: 9984 }, { amount: 150000, tenure: 18, roi: 16.5, emi: 9550 }], redirect_url: 'https://moneyview.in/apply?ref=MV-9001' }, 'offer'),
  t('mv_t_offers_pre', '/v2/offers', 'Pre-approved', 200, { status: 'PRE_APPROVED', redirect_url: 'https://moneyview.in/apply?ref=MV-9001' }, 'approved'),
  t('mv_t_offers_pending', '/v2/offers', 'Still deciding', 202, { status: 'PENDING' }, 'pending'),
  t('mv_t_offers_rej', '/v2/offers', 'Rejected (bureau)', 200, { status: 'REJECTED', reason: 'BUREAU_SCORE' }, 'rejected'),
  t('mv_t_status_ok', '/v2/status', 'Approved', 200, { status: 'APPROVED', redirect_url: 'https://moneyview.in/apply?ref=MV-9001' }, 'approved'),
  t('mv_t_status_rej', '/v2/status', 'Rejected', 200, { status: 'REJECTED', reason: 'POLICY' }, 'rejected'),
  t('mv_t_status_pending', '/v2/status', 'In progress', 200, { status: 'IN_PROGRESS' }, 'pending'),
  t('mv_t_products_ok', '/v2/products', 'Two products', 200, { products: [{ code: 'PL' }, { code: 'SA' }] }, 'ok'),
]

const MV_TESTS_130: TestEvent[] = [
  ...MV_TESTS_120,
  t('mv_t_create_pan', '/v2/create-lead', 'Wrong PAN', 422, { code: 'INVALID_PAN', message: 'PAN format invalid' }, 'rejected'),
]

// ---- SmartCoin, FatakPay, Lender Y ------------------------------------------------------------------------------

const SC_CREATE = `${SDK}

/** Create lead: POST /leads (api-key header). */
module.exports = defineApi({
  title: 'Create lead',
  outcomes: ['ok', 'duplicate', 'rejected', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({ method: 'POST', url: ctx.env.LENDER_BASE_URL + '/leads', headers: { 'X-Api-Key': ctx.secret('LENDER_API_KEY'), 'Content-Type': 'application/json' }, body: { ref: ctx.ref, phone: applicant.mobile, pan: applicant.pan, income: applicant.incomeMonth, pincode: applicant.pincode }, timeoutMs: 8000 })
    const b = reply.body || {}
    if (reply.status === 200 && b.result === 'CREATED') return { outcome: 'ok', reply_code: 'CREATED', lender_ref: b.lead_ref, link: b.app_link }
    if (reply.status === 200 && b.result === 'EXISTS') return { outcome: 'duplicate', reply_code: 'EXISTS' }
    if (reply.status === 200 && b.result === 'NOT_ELIGIBLE') return { outcome: 'rejected', reply_code: b.reason || 'NOT_ELIGIBLE' }
    return { outcome: 'error', reply_code: 'HTTP_' + reply.status }
  },
})
`
const SC_STATUS = `${SDK}

/** Status: GET /leads/{ref}. */
module.exports = defineApi({
  title: 'Status',
  outcomes: ['approved', 'rejected', 'pending', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({ method: 'GET', url: ctx.env.LENDER_BASE_URL + '/leads/' + (ctx.prev && ctx.prev.lender_ref), headers: { 'X-Api-Key': ctx.secret('LENDER_API_KEY') }, timeoutMs: 5000 })
    const b = reply.body || {}
    if (b.state === 'APPROVED') return { outcome: 'approved', reply_code: 'APPROVED', link: b.app_link }
    if (b.state === 'REJECTED') return { outcome: 'rejected', reply_code: 'REJECTED' }
    if (b.state === 'PENDING') return { outcome: 'pending', reply_code: 'PENDING', retryAfterMs: 10000 }
    return { outcome: 'error', reply_code: 'HTTP_' + reply.status }
  },
})
`
const SC_HEALTH = MV_HEALTH.replace("reply.body.status === 'UP'", 'reply.body.ok === true')

const FP_CREATE = `${SDK}

/** Create application: POST /applications (basic auth). */
module.exports = defineApi({
  title: 'Create application',
  outcomes: ['ok', 'duplicate', 'rejected', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({ method: 'POST', url: ctx.env.LENDER_BASE_URL + '/applications', headers: { Authorization: 'Basic ' + ctx.secret('LENDER_BASIC_AUTH'), 'Content-Type': 'application/json' }, body: { partnerRef: ctx.ref, mobile: applicant.mobile, pan: applicant.pan, salary: applicant.incomeMonth }, timeoutMs: 8000 })
    const b = reply.body || {}
    if (reply.status === 201) return { outcome: 'ok', reply_code: 'CREATED', lender_ref: b.applicationId }
    if (reply.status === 409) return { outcome: 'duplicate', reply_code: 'DUPLICATE' }
    if (reply.status === 400 && b.error === 'NOT_SALARIED') return { outcome: 'rejected', reply_code: 'NOT_SALARIED', message: 'FatakPay is for salaried people.' }
    if (reply.status >= 500) return { outcome: 'error', reply_code: 'HTTP_' + reply.status, message: 'FatakPay is not answering. Try later.' }
    return { outcome: 'error', reply_code: 'HTTP_' + reply.status }
  },
})
`

const LY_CREATE = `${SDK}
const { digitsOnly } = require('../../services/format')

/** Create lead (UAT only so far): POST /leads. */
module.exports = defineApi({
  title: 'Create lead',
  outcomes: ['ok', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({ method: 'POST', url: ctx.env.LENDER_BASE_URL + '/leads', headers: { 'Content-Type': 'application/json' }, body: { ref: ctx.ref, mobile: digitsOnly(applicant.mobile) }, timeoutMs: 8000 })
    if (reply.status === 200) return { outcome: 'ok', reply_code: 'OK', lender_ref: reply.body && reply.body.id }
    return { outcome: 'error', reply_code: 'HTTP_' + reply.status }
  },
})
`
const LY_STATUS = `${SDK}

/** Status: GET /leads/{id}. Not on the canvas yet. */
module.exports = defineApi({
  title: 'Status',
  outcomes: ['approved', 'pending', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({ method: 'GET', url: ctx.env.LENDER_BASE_URL + '/leads/' + (ctx.prev && ctx.prev.lender_ref), headers: {}, timeoutMs: 5000 })
    const b = reply.body || {}
    if (reply.status === 200 && b.state === 'APPROVED') return { outcome: 'approved', reply_code: 'APPROVED', link: b.link }
    if (reply.status === 200 && b.state === 'PENDING') return { outcome: 'pending', reply_code: 'PENDING', retryAfterMs: 10000 }
    return { outcome: 'error', reply_code: 'HTTP_' + reply.status }
  },
})
`
const LY_FORMAT = `/** src/services/format.js: helpers for this batch. */
function digitsOnly(text) {
  return String(text || '').replace(/[^0-9]/g, '')
}
module.exports = { digitsOnly }
`

// ---- Batches ----------------------------------------------------------------------------------------------------

type BatchSeed = Omit<Batch, 'manifest'>
const withManifest = (b: BatchSeed): Batch => ({ ...b, manifest: staticDiscover(b.files, b.updatedAt) })

export const BATCHES: Batch[] = ([
  {
    id: 'b_mv_130', lenderSlug: 'moneyview', version: '1.3.0', status: 'draft', madeFrom: '1.2.0',
    files: mvFiles(MV_CREATE_LEAD_130, true, '1.3.0'), tests: MV_TESTS_130,
    testRun: { passed: 17, total: 18, at: '2026-10-17T10:06:00+05:30', by: 'editor@firstfund.local' },
    git: { branch: 'moneyview/batchApi/1.3.0', tag: null, commit: 'c41e0a7' },
    bundle: null,
    commits: [
      { sha: 'c41e0a7', message: 'messages for low income and wrong PAN', who: 'editor@firstfund.local', at: '2026-10-17T10:05:00+05:30', files: ['src/api/v2/create-lead.js'] },
      { sha: '8d20f11', message: 'New version from 1.2.0', who: 'editor@firstfund.local', at: '2026-10-16T17:30:00+05:30', files: [] },
    ],
    createdBy: 'editor@firstfund.local', createdAt: '2026-10-16T17:30:00+05:30', updatedAt: '2026-10-17T10:05:00+05:30', madeLiveAt: null,
  },
  {
    id: 'b_mv_120', lenderSlug: 'moneyview', version: '1.2.0', status: 'live', madeFrom: '1.1.0',
    files: mvFiles(MV_CREATE_LEAD_120, true, '1.2.0'), tests: MV_TESTS_120,
    testRun: { passed: 17, total: 17, at: '2026-10-09T15:28:00+05:30', by: 'admin@firstfund.local' },
    git: { branch: 'moneyview/batchApi/1.2.0', tag: 'moneyview/batchApi/1.2.0', commit: '9b2f1d3' },
    bundle: { key: 'bundles/moneyview/1.2.0.tgz', checksum: 'sha256:7c1e4b9a02d6f3e8c5a1b7d9e0f2a4c6b8d0e2f4a6c8e0b2d4f6a8c0e2b4d6f8', builtAt: '2026-10-09T15:30:00+05:30', builtBy: 'admin@firstfund.local' },
    commits: [
      { sha: '9b2f1d3', message: 'products endpoint for salary advance', who: 'editor@firstfund.local', at: '2026-10-09T14:50:00+05:30', files: ['src/api/v2/products.js', 'README.md'] },
      { sha: '3f0ab77', message: 'offers: PENDING → pending with retry', who: 'editor@firstfund.local', at: '2026-10-08T11:10:00+05:30', files: ['src/api/v2/offers.js'] },
      { sha: 'a1c9e02', message: 'New version from 1.1.0', who: 'admin@firstfund.local', at: '2026-10-07T09:00:00+05:30', files: [] },
    ],
    createdBy: 'admin@firstfund.local', createdAt: '2026-10-07T09:00:00+05:30', updatedAt: '2026-10-09T15:45:00+05:30', madeLiveAt: '2026-10-09T15:45:00+05:30',
  },
  {
    id: 'b_mv_110', lenderSlug: 'moneyview', version: '1.1.0', status: 'previous', madeFrom: '1.0.0',
    files: mvFiles(MV_CREATE_LEAD_120, false, '1.1.0', MV_OFFERS_110), tests: MV_TESTS_120.filter((x) => x.endpoint !== '/v2/products' && x.id !== 'mv_t_offers_pending'),
    testRun: { passed: 15, total: 15, at: '2026-10-02T09:40:00+05:30', by: 'admin@firstfund.local' },
    git: { branch: 'moneyview/batchApi/1.1.0', tag: 'moneyview/batchApi/1.1.0', commit: '51aa90e' },
    bundle: { key: 'bundles/moneyview/1.1.0.tgz', checksum: 'sha256:a90d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0d', builtAt: '2026-10-02T09:45:00+05:30', builtBy: 'admin@firstfund.local' },
    commits: [{ sha: '51aa90e', message: 'v2 API: dedupe, create-lead, offers, status with the bearer token', who: 'editor@firstfund.local', at: '2026-10-01T16:00:00+05:30', files: ['src/api/v2/token.js', 'src/api/v2/dedupe.js', 'src/api/v2/create-lead.js', 'src/api/v2/offers.js', 'src/api/v2/status.js'] }],
    createdBy: 'admin@firstfund.local', createdAt: '2026-09-30T10:00:00+05:30', updatedAt: '2026-10-09T15:45:00+05:30', madeLiveAt: '2026-10-02T10:00:00+05:30',
  },
  {
    id: 'b_mv_100', lenderSlug: 'moneyview', version: '1.0.0', status: 'retired', madeFrom: null,
    files: [{ path: 'package.json', content: packageJsonTemplate('moneyview', '1.0.0') }, { path: 'src/server.js', content: serverTemplate() }, { path: 'src/app.js', content: appTemplate() }, { path: 'src/api/health.js', content: MV_HEALTH }, { path: 'src/api/v1/lead.js', content: MV_OLD_LEAD }, { path: 'src/api/v1/eligibility.js', content: MV_OLD_ELIGIBILITY }, { path: 'README.md', content: '# moneyview.batchApi 1.0.0\n\nThe old v1 API with an api-key header.\n' }],
    tests: [
      t('mv_t_old_lead_ok', '/v1/lead', 'Lead ok', 200, { id: 'L-1' }, 'ok'),
      t('mv_t_old_lead_err', '/v1/lead', 'Server error', 500, {}, 'error'),
      t('mv_t_old_elig_ok', '/v1/eligibility', 'Eligible', 200, { eligible: true }, 'ok'),
    ],
    testRun: { passed: 3, total: 3, at: '2026-09-20T11:00:00+05:30', by: 'admin@firstfund.local' },
    git: { branch: 'moneyview/batchApi/1.0.0', tag: 'moneyview/batchApi/1.0.0', commit: '0e77c21' },
    bundle: { key: 'bundles/moneyview/1.0.0.tgz', checksum: 'sha256:0e77c21d4f6a8b0c2e4f6a8b0c2d4e6f8a0b2c4d6e8f0a2b4c6d8e0f2a4b6c8d', builtAt: '2026-09-20T11:05:00+05:30', builtBy: 'admin@firstfund.local' },
    commits: [{ sha: '0e77c21', message: 'first batch: v1 lead + eligibility', who: 'admin@firstfund.local', at: '2026-09-20T10:30:00+05:30', files: ['src/api/v1/lead.js', 'src/api/v1/eligibility.js', 'src/api/health.js'] }],
    createdBy: 'admin@firstfund.local', createdAt: '2026-09-20T10:00:00+05:30', updatedAt: '2026-10-02T10:00:00+05:30', madeLiveAt: '2026-09-20T11:10:00+05:30',
  },
  {
    id: 'b_sc_201', lenderSlug: 'smartcoin', version: '2.0.1', status: 'live', madeFrom: '2.0.0',
    files: [{ path: 'package.json', content: packageJsonTemplate('smartcoin', '2.0.1') }, { path: 'src/server.js', content: serverTemplate() }, { path: 'src/app.js', content: appTemplate() }, { path: 'src/api/health.js', content: SC_HEALTH }, { path: 'src/api/v1/create-lead.js', content: SC_CREATE }, { path: 'src/api/v1/status.js', content: SC_STATUS }, { path: 'README.md', content: '# smartcoin.batchApi\n\nApi-key header on every call (LENDER_API_KEY); no token source.\n' }],
    tests: [
      t('sc_t_health', '/health', 'Lender up', 200, { ok: true }, 'ok'),
      t('sc_t_create_ok', '/v1/create-lead', 'Created', 200, { result: 'CREATED', lead_ref: 'SC-77', app_link: 'https://smartcoin.co.in/app?r=SC-77' }, 'ok'),
      t('sc_t_create_dup', '/v1/create-lead', 'Exists', 200, { result: 'EXISTS' }, 'duplicate'),
      t('sc_t_create_rej', '/v1/create-lead', 'Not eligible', 200, { result: 'NOT_ELIGIBLE', reason: 'PINCODE' }, 'rejected'),
      t('sc_t_status_appr', '/v1/status', 'Approved', 200, { state: 'APPROVED', app_link: 'https://smartcoin.co.in/app?r=SC-77' }, 'approved'),
      t('sc_t_status_pend', '/v1/status', 'Pending', 200, { state: 'PENDING' }, 'pending'),
      t('sc_t_status_rej', '/v1/status', 'Rejected', 200, { state: 'REJECTED' }, 'rejected'),
    ],
    testRun: { passed: 7, total: 7, at: '2026-10-08T11:50:00+05:30', by: 'admin@firstfund.local' },
    git: { branch: 'smartcoin/batchApi/2.0.1', tag: 'smartcoin/batchApi/2.0.1', commit: '77be1a0' },
    bundle: { key: 'bundles/smartcoin/2.0.1.tgz', checksum: 'sha256:5e1c7a9b3d5f7a9b1c3d5e7f9a1b3c5d7e9f1a3b5c7d9e1f3a5b7c9d1e3f5a7b', builtAt: '2026-10-08T11:55:00+05:30', builtBy: 'admin@firstfund.local' },
    commits: [{ sha: '77be1a0', message: 'status: PENDING retry 10 s', who: 'editor@firstfund.local', at: '2026-10-08T11:40:00+05:30', files: ['src/api/v1/status.js'] }],
    createdBy: 'admin@firstfund.local', createdAt: '2026-10-08T10:00:00+05:30', updatedAt: '2026-10-08T12:00:00+05:30', madeLiveAt: '2026-10-08T12:00:00+05:30',
  },
  {
    id: 'b_fp_104', lenderSlug: 'fatakpay', version: '1.0.4', status: 'live', madeFrom: '1.0.3',
    files: [{ path: 'package.json', content: packageJsonTemplate('fatakpay', '1.0.4') }, { path: 'src/server.js', content: serverTemplate() }, { path: 'src/app.js', content: appTemplate() }, { path: 'src/api/health.js', content: SC_HEALTH }, { path: 'src/api/v1/create-application.js', content: FP_CREATE }, { path: 'README.md', content: '# fatakpay.batchApi\n\nBasic auth from the Env variables tab (LENDER_BASIC_AUTH); the create API answers 502 since 2026-10-17 08:50 (lender Off).\n' }],
    tests: [
      t('fp_t_create_ok', '/v1/create-application', 'Created', 201, { applicationId: 'FP-1' }, 'ok'),
      t('fp_t_create_dup', '/v1/create-application', 'Duplicate', 409, {}, 'duplicate'),
      t('fp_t_create_rej', '/v1/create-application', 'Not salaried', 400, { error: 'NOT_SALARIED' }, 'rejected'),
      t('fp_t_create_502', '/v1/create-application', 'Gateway 502', 502, null, 'error', 'captured'),
    ],
    testRun: { passed: 4, total: 4, at: '2026-09-28T12:00:00+05:30', by: 'admin@firstfund.local' },
    git: { branch: 'fatakpay/batchApi/1.0.4', tag: 'fatakpay/batchApi/1.0.4', commit: 'e4d0c33' },
    bundle: { key: 'bundles/fatakpay/1.0.4.tgz', checksum: 'sha256:e4d0c33f1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d', builtAt: '2026-09-28T12:05:00+05:30', builtBy: 'admin@firstfund.local' },
    commits: [{ sha: 'e4d0c33', message: 'create-application: 5xx → friendly message', who: 'editor@firstfund.local', at: '2026-09-28T11:30:00+05:30', files: ['src/api/v1/create-application.js'] }],
    createdBy: 'admin@firstfund.local', createdAt: '2026-09-28T10:00:00+05:30', updatedAt: '2026-09-28T12:10:00+05:30', madeLiveAt: '2026-09-28T12:10:00+05:30',
  },
  {
    id: 'b_ly_030', lenderSlug: 'lendery', version: '0.3.0', status: 'draft', madeFrom: '0.2.0',
    files: [
      { path: 'package.json', content: packageJsonTemplate('lendery', '0.3.0') }, { path: 'src/server.js', content: serverTemplate() }, { path: 'src/app.js', content: appTemplate() },
      { path: 'src/api/v1/create-lead.js', content: LY_CREATE },
      { path: 'src/api/v1/status.js', content: LY_STATUS },
      { path: 'src/services/format.js', content: LY_FORMAT },
      { path: 'README.md', content: '# lendery.batchApi\n\nUAT only; the lender has not confirmed our IPs yet. Two endpoints under src/api/v1/, a helper in src/services/.\n' },
    ],
    tests: [
      t('ly_t_create_ok', '/v1/create-lead', 'Lead ok', 200, { id: 'LY-1' }, 'ok'),
      t('ly_t_create_err', '/v1/create-lead', 'Server error', 500, null, 'error'),
      t('ly_t_status_ok', '/v1/status', 'Approved', 200, { state: 'APPROVED', link: 'https://lendery.example/continue?r=LY-1' }, 'approved'),
      t('ly_t_status_pend', '/v1/status', 'Pending', 200, { state: 'PENDING' }, 'pending'),
    ],
    testRun: null,
    git: { branch: 'lendery/batchApi/0.3.0', tag: null, commit: '12ab9f0' },
    bundle: null,
    commits: [{ sha: '12ab9f0', message: 'create-lead and status against UAT', who: 'editor@firstfund.local', at: '2026-10-14T11:00:00+05:30', files: ['src/api/v1/create-lead.js', 'src/api/v1/status.js', 'src/services/format.js'] }],
    createdBy: 'editor@firstfund.local', createdAt: '2026-10-14T10:30:00+05:30', updatedAt: '2026-10-14T11:00:00+05:30', madeLiveAt: null,
  },
] as BatchSeed[]).map(withManifest)

// ---- API records (3.1) ------------------------------------------------------------------------------------------

const api = (lenderSlug: string, id: string, name: string, endpoint: string, method: LenderApi['method'], path: string, auth: LenderApi['auth'], authNote: string, whitelist: LenderApi['whitelist'], status: LenderApi['status'], usedIn: string, lastCall: LenderApi['lastCall'], more: Partial<Pick<LenderApi, 'answer' | 'fields'>> = {}): LenderApi => ({ id, lenderSlug, name, endpoint, method, path, auth, authNote, whitelist, status, usedIn, lastCall, answer: 'none', fields: [], ...more })

export const APIS: LenderApi[] = [
  api('moneyview', 'api_mv_health', 'Health check', '/health', 'GET', '/health', 'none', 'none', { state: 'done', date: '2026-10-12' }, 'active', 'monitor, every 60 s', { at: '2026-10-17T10:19:00+05:30', httpStatus: 200 }),
  api('moneyview', 'api_mv_token', 'Auth token', '/v2/token', 'POST', '/oauth/token', 'token_source', 'PARTNER_ID + CLIENT_SECRET → bearer 55 min', { state: 'done', date: '2026-10-12' }, 'active', 'all (auto)', { at: '2026-10-17T10:08:00+05:30', httpStatus: 200 }),
  api('moneyview', 'api_mv_dedupe', 'Dedupe check', '/v2/dedupe', 'POST', '/lead/dedupe', 'bearer', 'bearer', { state: 'done', date: '2026-10-12' }, 'active', 'pl.flow 1.3.0, 1.4.0', { at: '2026-10-17T10:17:00+05:30', httpStatus: 200 }),
  api('moneyview', 'api_mv_create', 'Create lead (pre-fill)', '/v2/create-lead', 'POST', '/lead/create', 'bearer', 'bearer', { state: 'done', date: '2026-10-12' }, 'active', 'pl.flow 1.3.0, 1.4.0 · sa.flow 1.0.0', { at: '2026-10-17T10:17:00+05:30', httpStatus: 201 }, {
    // J2: the extra fields this API needs, and how MoneyView names and codes them (demo values)
    fields: [
      { key: 'employerName', required: true, lenderKey: 'employerName', codes: {} },
      { key: 'residenceType', required: false, lenderKey: 'address.residenceType', codes: { rented: 'RENTED', owned: 'OWNED', 'family-owned': 'OWNED_BY_FAMILY' } },
    ],
  }),
  api('moneyview', 'api_mv_offers', 'Check offers', '/v2/offers', 'POST', '/lead/{id}/offer', 'bearer', 'bearer', { state: 'done', date: '2026-10-12' }, 'active', 'pl.flow 1.3.0, 1.4.0', { at: '2026-10-17T10:17:00+05:30', httpStatus: 200 }, { answer: 'one' }),
  api('moneyview', 'api_mv_status', 'Status', '/v2/status', 'GET', '/lead/{id}/status', 'bearer', 'bearer', { state: 'done', date: '2026-10-12' }, 'active', 'pl.flow 1.3.0 (poll)', { at: '2026-10-17T10:12:00+05:30', httpStatus: 200 }),
  api('moneyview', 'api_mv_products', 'Fetch products', '/v2/products', 'GET', '/products', 'bearer', 'bearer', { state: 'done', date: '2026-10-12' }, 'active', 'sa.flow 1.0.0', { at: '2026-10-17T08:20:00+05:30', httpStatus: 200 }),
  api('moneyview', 'api_mv_old_lead', 'Lead create (old)', '/v1/lead', 'POST', '/v1/lead', 'api_key_header', 'api-key header', { state: 'done', date: '2026-09-20' }, 'old_kept', 'pl.flow 1.0.0, 1.1.0 (retired)', { at: '2026-09-30T18:00:00+05:30', httpStatus: 200 }),
  api('moneyview', 'api_mv_old_elig', 'Eligibility (old)', '/v1/eligibility', 'POST', '/v1/eligibility', 'api_key_header', 'api-key header', { state: 'lender_removed', date: '2026-08-12' }, 'old_unused', '—', { at: '2026-08-12T09:00:00+05:30', httpStatus: 410 }),

  api('smartcoin', 'api_sc_health', 'Health check', '/health', 'GET', '/health', 'none', 'none', { state: 'done', date: '2026-10-08' }, 'active', 'monitor, every 60 s', { at: '2026-10-17T10:19:00+05:30', httpStatus: 200 }),
  api('smartcoin', 'api_sc_create', 'Create lead', '/v1/create-lead', 'POST', '/leads', 'api_key_header', 'X-Api-Key', { state: 'done', date: '2026-10-08' }, 'active', 'pl.flow 2.1.0', { at: '2026-10-17T10:14:00+05:30', httpStatus: 200 }),
  api('smartcoin', 'api_sc_status', 'Status', '/v1/status', 'GET', '/leads/{ref}', 'api_key_header', 'X-Api-Key', { state: 'done', date: '2026-10-08' }, 'active', 'pl.flow 2.1.0 (poll)', { at: '2026-10-17T10:15:00+05:30', httpStatus: 200 }),

  api('fatakpay', 'api_fp_health', 'Health check', '/health', 'GET', '/health', 'none', 'none', { state: 'done', date: '2026-09-28' }, 'active', 'monitor, every 60 s', { at: '2026-10-17T10:19:00+05:30', httpStatus: 502 }),
  api('fatakpay', 'api_fp_create', 'Create application', '/v1/create-application', 'POST', '/applications', 'basic', 'basic auth (LENDER_BASIC_AUTH)', { state: 'done', date: '2026-09-28' }, 'active', 'pl.flow 1.2.0 (suspended)', { at: '2026-10-17T08:51:00+05:30', httpStatus: 502 }),

  api('lendery', 'api_ly_create', 'Create lead', '/v1/create-lead', 'POST', '/leads', 'none', 'none (UAT)', { state: 'requested', date: null }, 'active', 'bl.flow 0.3.0 (draft)', null),
  api('lendery', 'api_ly_status', 'Status', '/v1/status', 'GET', '/leads/{id}', 'none', 'none (UAT)', { state: 'requested', date: null }, 'active', '—', null),
]

// ---- Captured codes and reply shapes (G11) ----------------------------------------------------------------------

export const CAPTURED: CapturedCode[] = [
  { id: 'cap_mv_1', lenderSlug: 'moneyview', code: 'LEAD_CREATED', endpoint: '/v2/create-lead', uat: 61, prod: 402, lastSeen: '2026-10-17T10:17:00+05:30', known: 'known', outcome: 'ok', alertId: null, sampleReply: { status: 201, body: { status: 'LEAD_CREATED', lead_id: 'MV-****' } } },
  { id: 'cap_mv_2', lenderSlug: 'moneyview', code: 'DUP_CUSTOMER', endpoint: '/v2/dedupe', uat: 9, prod: 37, lastSeen: '2026-10-17T09:50:00+05:30', known: 'known', outcome: 'duplicate', alertId: null, sampleReply: { status: 200, body: { exists: true, code: 'DUP_CUSTOMER' } } },
  { id: 'cap_mv_3', lenderSlug: 'moneyview', code: 'BUREAU_HIT_FAIL', endpoint: '/v2/offers', uat: 0, prod: 3, lastSeen: '2026-10-16T18:40:00+05:30', known: 'new', outcome: null, alertId: 'a1', sampleReply: { status: 200, body: { status: 'REJECTED', reason: 'BUREAU_HIT_FAIL' } } },
  { id: 'cap_mv_4', lenderSlug: 'moneyview', code: '{ status, offers: {…} }', endpoint: '/v2/offers', uat: 2, prod: 0, lastSeen: '2026-10-15T14:00:00+05:30', known: 'new_shape', outcome: null, alertId: null, sampleReply: { status: 200, body: { status: 'OFFERS', offers: { amount: 150000, tenure: 12, roi: 18, emi: 13750 } } } },
  { id: 'cap_fp_1', lenderSlug: 'fatakpay', code: 'HTTP_502', endpoint: '/v1/create-application', uat: 0, prod: 14, lastSeen: '2026-10-17T08:51:00+05:30', known: 'known', outcome: 'error', alertId: 'a3', sampleReply: { status: 502, body: null } },
]
